import Head from 'expo-router/head';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, XStack, YStack } from 'tamagui';

import SecretMenuCard, {
  type SecretMenuData,
} from '@/components/SecretMenuCard';

/**
 * ── /components/secret-menu-card ─────────────────────────────────────────
 *
 * SecretMenuCard — the Access-Pass "secret menu" tile. Same anatomy as
 * EventCard (3:4 poster + info panel) plus a floating round logo badge, and
 * varies on a single `state` union (`available` / `dropping-soon` /
 * `sold-out`) instead of EventCard's `soldOut` flag — the panel fill/copy is
 * fully determined by state, so there's no `panelColor` prop to preview (see
 * the component's own docstring).
 *
 * There is no real screen rendering this yet — `src/api/eventsApi.ts`
 * already fetches `payload.secretMenus` and `HomeSkeleton.tsx` already
 * reserves a row's worth of height for it (`SecretMenuCardsSkeleton`), but
 * the home screen (`app/index.tsx`) doesn't wire a live section in yet. This
 * preview is the only place the three states can currently be eyeballed
 * together.
 *
 * Takes each slot's RAW ISO `slotDateTime` and formats internally via
 * `formatDateTime` (`src/utils/dateFormatter.ts`) — fixtures below pass raw
 * ISO strings, never pre-formatted captions, per the house rule.
 * ─────────────────────────────────────────────────────────────────────────
 */

// One menu per state so all three lay out at once in the row below. Mixed
// title lengths (one wraps to 2 lines, two don't) to eyeball uneven card
// heights in a stretched row — same intent as the old all.tsx fixture set.
const PREVIEW_MENUS: (SecretMenuData & {
  id: string;
  state: 'available' | 'dropping-soon' | 'sold-out';
})[] = [
  {
    id: 'sm-available',
    imageUrl: 'https://picsum.photos/id/1060/600/800',
    logoUrl: 'https://picsum.photos/id/431/96/96',
    name: 'The Table Omakase',
    // A few days out → exercises the absolute "EEE d MMM" branch of
    // formatDateTime rather than the "Today"/"Tomorrow" relative branches.
    slotDateTime: '2026-09-28T19:30:00',
    state: 'available',
  },
  {
    id: 'sm-dropping',
    imageUrl: 'https://picsum.photos/id/1078/600/800',
    logoUrl: 'https://picsum.photos/id/312/96/96',
    name: "Ummi's Kitchen — Home-Style Awadhi Supper Club",
    state: 'dropping-soon',
  },
  {
    id: 'sm-soldout',
    imageUrl: 'https://picsum.photos/id/292/600/800',
    logoUrl: 'https://picsum.photos/id/225/96/96',
    name: 'Hosa',
    state: 'sold-out',
  },
];

export default function SecretMenuCardPreview() {
  return (
    <>
      <Head>
        <title>SecretMenuCard · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* One card per state, captioned, in the same horizontal-scroll shape
            as the home carousels (see all.tsx's event-carousel block above
            where this used to live). items="stretch" matches every card to
            the row's tallest; each card is wrapped in its own YStack flex={1}
            so it actually GROWS to fill that stretched row height — the card
            itself only opts into alignSelf="stretch" (see its docstring),
            which fills a parent that's already tall, it doesn't make one tall
            on its own. The real screen (once wired) renders cards directly
            with no such wrapper, since its row stretches from sibling cards
            alone. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              available (brand panel, formatted next slot) · dropping-soon
              (neutral, dimmed, hardwired caption) · sold-out (neutral, dimmed,
              badge instead of caption)
            </Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <XStack gap="$md" items="stretch">
                {PREVIEW_MENUS.map((menu) => (
                  <YStack key={menu.id} flex={1}>
                    <SecretMenuCard menu={menu} state={menu.state} />
                  </YStack>
                ))}
              </XStack>
            </ScrollView>
          </YStack>

          {/* Both the title and the caption are numberOfLines={2} — the title
            clamp is easy to miss in the row above since only the dropping-soon
            fixture wraps at all, and at the default 240 width even that one
            only just reaches 2 lines. Forcing a narrower `width` here with a
            deliberately longer name makes the clamp itself (not just the
            wrap) visible: the card should grow no taller than 2 title lines
            allow, truncating with an ellipsis rather than pushing the panel
            down further. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              narrow width + long name — title clamps at 2 lines (ellipsis),
              doesn&apos;t keep growing the panel
            </Text>
            <SecretMenuCard
              width={160}
              menu={{
                imageUrl: 'https://picsum.photos/id/1060/600/800',
                logoUrl: 'https://picsum.photos/id/431/96/96',
                name: "The Late Night Chef's Table Omakase Supper Club Experience",
                slotDateTime: '2026-09-28T19:30:00',
              }}
              state="available"
            />
          </YStack>

          {/* `slotDateTime` is typed optional (see SecretMenuData) since the
            two disabled states ignore it — but nothing stops a caller from
            passing `state="available"` WITHOUT one. The component guards
            against crashing the formatter, but the result is a card with an
            empty second line: title only, no "Next slot" caption at all. This
            is a real gap between the type and the component's own contract
            (available implies a next slot exists), not a state a real screen
            should intentionally produce — shown here so it isn't a surprise
            in a live app. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              gotcha — available with no `slotDateTime`: renders with no caption
              at all, instead of falling back to dropping-soon&apos;s copy
            </Text>
            <SecretMenuCard
              menu={{
                imageUrl: 'https://picsum.photos/id/1078/600/800',
                logoUrl: 'https://picsum.photos/id/312/96/96',
                name: 'Undisclosed Location',
              }}
              state="available"
            />
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
